/**
 * Precedent: PEPM Project Closure — `apps/portal/web/src/pages/project_management/tracking/ProjectCompletion.tsx:450-473`
 * (+ `ProjectCompletion.css:111-141`). Cloned, not imported.
 *
 * SCREEN_MAP #3 — INI-01-03, MODE-01 — resolved by **UI_GAPS.md owner decision #7's
 * recommendation: the readiness checklist, not the wizard.** Its four reasons are in that
 * document; the first is the one that decides it, and it is §6.1's own words: a capability model
 * that does not balance "can be saved as draft but cannot be baselined, and steps 4-9 stay
 * locked" — a set of conditions evaluated continuously, not a path walked once.
 *
 * WHAT IS CLONED, LINE FOR LINE, FROM `ProjectCompletion.tsx:450-473`:
 *   · `conds` as an array of `{ ok, label }`;
 *   · `met` / `total`, and the THREE-WAY banner state — `ready` when all are met, the plain
 *     blue state when one is outstanding, `blocked` below that;
 *   · the three icons ✅ 🔵 ⚠️ in that mapping;
 *   · the title: "Ready to formally close" becomes this screen's equivalent, and otherwise
 *     `${met} of ${total} … conditions met`;
 *   · the subtitle, which lists the outstanding conditions joined with ' · ' rather than
 *     counting them — the same habit `ApplicationMasterDataTab.tsx:803-804` states as a rule:
 *     "Rejected rows are listed rather than counted: '12 skipped' tells you something is wrong,
 *     not which line to fix."
 *
 * WHERE THE CONDITIONS COME FROM — every one is a precondition the server already enforces, so
 * this screen cannot drift from the gate it describes:
 *   1. §6.12 / §4.8.1 — a baselined capability model must exist.
 *   2. §6.1 — every sibling set totals exactly 100.0%. Reported by `weights_balanced` (§4.3).
 *   3. §4.8.2 — at least one assessment unit.
 *   4. §4.8.3 — not already baselined.
 * The labels are AUTHORED (UI_CONVENTIONS §3.7: this module has no source app to lift wording
 * from). The two strings that are NOT authored are §6.1's, and they do not appear on this screen
 * — they belong to the weight editor, where the deficit is known to the tenth.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * THE SAME CHECKLIST IN TWO PLACES, ONE COPY OF IT (2026-10-05).
 *
 * Owner: *"when I hit setting, I need to see right panel design. Sames with Edit button."* So the
 * ⚙️ row action on the initiative list no longer navigates away — it opens this checklist as a
 * RIGHT-SIDE PANEL over the list (DECISIONS.md D-017, *"in general please replace all windows
 * from this app by 'right panel'"*).
 *
 * The file therefore exports THREE things rather than one, and the split is at exactly the seam
 * the two presentations differ at — the page chrome:
 *
 *   · `InitiativeSetupContent` — the banner, the conditions card, the two links and the gated
 *     Baseline action. Every line of it is the markup that was already here, unmoved, and it
 *     loads its own `/context` and initiative exactly as before, so neither presentation can
 *     drift from the other.
 *   · `InitiativeSetup` — THE STANDALONE SCREEN, UNCHANGED and still routed at
 *     `/erp_consolidation/initiatives/:id/setup` by `ErpConsolidationModule.tsx:164`. It is kept
 *     because removing a route nobody asked to remove is the CLAUDE.md "additive change only"
 *     failure, and because a link to a specific initiative's readiness still has to open.
 *   · `InitiativeSetupPanel` — the same content inside `chrome/FormPanel`, SINGLE width.
 *
 * WHY THE PANEL'S FOOTER HAS NO PRIMARY (`hideSave`). This checklist is not a form: it has no
 * fields and nothing to save. Its one action is **Baseline initiative**, and that action is
 * GATED on all five conditions with the reason beside it — a state `FormPanel`'s footer cannot
 * express, because its primary is disabled only by `busy`, which also disables Cancel. So the
 * action stays exactly where it already is, inside the card, with its gate and its reason
 * intact, and the footer carries Cancel alone — the configuration `FormPanel.tsx:106-110`
 * already describes ("Cancel is then the only control").
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { FormPanel } from '../chrome/FormPanel';
import { erpApi } from '../api';
import { type ErpContext, type Initiative } from '../types';
import { canAdministerInitiatives } from '../roles';
import './InitiativeSetup.css';

/*
 * EXPORTED, so the initiatives list can show it as a second tab on its own help drawer.
 *
 * The reason: ⚙️ now opens the setup checklist as a right-side PANEL, and a panel has nowhere to
 * hang a "How this works" launcher — the screen behind it already has one pinned in the same corner,
 * so two would sit on top of each other. Without this the only way to reach this content would be to
 * navigate to the standalone route directly, which nobody does. `HelpPanel` already supports a tab
 * strip (`chrome/HelpPanel.tsx:21-23`, `helps[]`), so the list passes both panes and the content is
 * one click from where the panel is opened.
 */
export const SetupHelp: Help = {
  title: 'Initiative Setup',
  icon: '✅',
  purpose:
    'Everything that has to be true before an initiative can be <strong>baselined</strong> — and baselining is what takes the snapshot every later score is evidenced against. This is a checklist, not a wizard: the conditions are re-read every time you open the screen, and they can be satisfied in any order.',
  doHere: [
    'Read what is outstanding. Each line is a condition the server itself enforces, so nothing here can pass while the baseline would fail.',
    'Open the <strong>capability model</strong> to allocate weights — every level must total exactly <strong>100.0%</strong>.',
    'Open <strong>business units</strong> to build the organisation this initiative is assessed across.',
    'When every condition is met, <strong>Baseline initiative</strong> freezes the operating mode and captures one snapshot per system in scope.',
  ],
  fits: 'ERP Consolidation &rarr; Initiatives &rarr; Setup',
  firstTime:
    'Build the capability model first. Until its weights balance it cannot be baselined, and until it is baselined the initiative cannot be either.',
  sections: [
    {
      type: 'html',
      label: 'What baselining does, and what it stops',
      html: "Baselining sets the initiative's <strong>operating mode permanently</strong> and writes one <strong>snapshot per system</strong>, so a published result records which world it was computed in. After it, the mode cannot change and the initiative cannot be deleted. The capability model is baselined <em>separately and first</em> — changing a baselined model creates a new version rather than editing it.",
    },
    {
      type: 'html',
      label: 'What this screen does not do',
      html: 'It does not select systems. That screen is a later phase, so the "system in scope" condition is reported here and satisfied elsewhere — said plainly rather than left as a dead link.',
    },
  ],
};

interface Condition {
  readonly ok: boolean;
  readonly label: string;
  readonly hint?: string;
}

/** APR's hand-drawn markers — `pages/apr/components/RowControls.tsx:255-270`. */
function ConditionMarker({ ok }: { ok: boolean }) {
  return ok ? (
    <svg
      className="erp-cond__marker"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.5 5.3 L3.9 7.7 L8.5 2.3" />
    </svg>
  ) : (
    <svg className="erp-cond__marker" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <circle cx="5" cy="5" r="4.1" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

export interface InitiativeSetupContentProps {
  initiativeId: string;
  /**
   * Handed the initiative once it has loaded, so the STANDALONE screen's `PageHeader` can keep
   * naming it in its subtitle exactly as it did when the load lived in that component. Must be
   * stable across renders — a `useState` setter is, which is what both call sites pass.
   */
  onInitiativeLoaded?: (initiative: Initiative) => void;
  /** Called after a successful baseline, so a list the panel is open over can re-read its rows. */
  onBaselined?: () => void;
}

/**
 * The checklist itself — everything that was inside `.erp-setup-body`, unmoved.
 *
 * It owns its own loads (`/context` scoped to this initiative, and the initiative) rather than
 * taking them as props, because that is what the standalone screen already did and because a
 * second source for either would be a second thing to keep in step.
 */
export function InitiativeSetupContent({
  initiativeId,
  onInitiativeLoaded,
  onBaselined,
}: InitiativeSetupContentProps) {
  const navigate = useNavigate();

  const [context, setContext] = useState<ErpContext | null>(null);
  const [initiative, setInitiative] = useState<Initiative | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [contextPayload, detail] = await Promise.all([
        erpApi.context(initiativeId),
        erpApi.initiatives.get(initiativeId),
      ]);
      setContext(contextPayload.context);
      setInitiative(detail.initiative);
      if (onInitiativeLoaded) onInitiativeLoaded(detail.initiative);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load this initiative');
    } finally {
      setLoading(false);
    }
  }, [initiativeId, onInitiativeLoaded]);

  useEffect(() => {
    void load();
  }, [load]);

  const baseline = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await erpApi.initiatives.baseline(initiativeId);
      setInitiative(payload.initiative);
      setNotice(
        `Baselined. Snapshot version ${payload.snapshot.version} captured ${payload.snapshot.applications} system${payload.snapshot.applications === 1 ? '' : 's'}.`,
      );
      /* A baselined initiative loses its Edit and Delete row actions (§4.7), so a list this
         panel is open over has to re-read rather than keep showing controls the server refuses. */
      if (onBaselined) onBaselined();
    } catch (err) {
      /* §1.6: the server's `message` is display-ready and is shown unaltered. */
      setError(err instanceof Error ? err.message : 'Could not baseline this initiative');
    } finally {
      setBusy(false);
    }
  };

  const conds: Condition[] = initiative
    ? [
        {
          ok: initiative.capability_model_count > 0,
          label: 'A capability model exists',
          hint: 'The model is the tree every system is weighed against.',
        },
        {
          ok: initiative.weights_balanced === true,
          label: 'Every weight group totals exactly 100.0%',
          hint: 'Checked at every level: the functional areas, and the children of each capability.',
        },
        {
          ok: initiative.latest_capability_model_status === 'baselined',
          label: 'The capability model is baselined',
          hint: 'Baseline the model first; a model that does not balance cannot be baselined at all.',
        },
        {
          ok: initiative.assessment_unit_count > 0,
          label: 'At least one system is in scope',
          hint: 'System selection arrives in a later phase; the condition is reported here either way.',
        },
        {
          ok: initiative.baselined_at === null,
          label: 'Not already baselined',
          hint: 'An initiative is baselined once. A later change creates a new version downstream.',
        },
      ]
    : [];

  const met = conds.filter((c) => c.ok).length;
  const total = conds.length;
  /* The precedent's three-way mapping, `ProjectCompletion.tsx:466-468`. */
  const bannerClass = met === total ? ' ready' : met >= total - 1 ? '' : ' blocked';
  const bannerIcon = met === total ? '✅' : met >= total - 1 ? '🔵' : '⚠️';
  const bannerTitle =
    met === total
      ? 'Ready to baseline this initiative'
      : `${met} of ${total} baseline conditions met`;
  const bannerSub =
    met === total
      ? 'Every condition is satisfied — baselining will freeze the operating mode and capture the snapshot.'
      : `Outstanding: ${conds
          .filter((c) => !c.ok)
          .map((c) => c.label)
          .join(' · ')}`;

  const allMet = total > 0 && met === total;
  const canBaseline = canAdministerInitiatives(context);
  const gateReason = allMet ? undefined : 'Every baseline condition must be met first';

  if (loading) return <p>Loading initiative…</p>;
  if (!initiative) return <p>{error ?? 'This initiative could not be read.'}</p>;

  return (
    <>
      <div className={`erp-banner${bannerClass}`}>
        <div className="erp-banner__icon">{bannerIcon}</div>
        <div>
          <div className="erp-banner__title">{bannerTitle}</div>
          <div className="erp-banner__sub">{bannerSub}</div>
        </div>
      </div>

      {error && (
        <div className="erp-panel-error" style={{ marginTop: 16 }}>
          ⚠️ {error}
        </div>
      )}
      {notice && (
        <div className="erp-banner ready" style={{ marginTop: 16 }}>
          <div className="erp-banner__icon">✅</div>
          <div>
            <div className="erp-banner__title">{notice}</div>
            <div className="erp-banner__sub">
              The operating mode is now frozen for this initiative.
            </div>
          </div>
        </div>
      )}

      <div className="erp-card">
        <div className="erp-card-content">
          <span className="erp-card-title">Baseline conditions</span>
          <p className="erp-section-sub">
            Each line is a condition the server enforces when you baseline. They are derived, so
            there is nothing to tick.
          </p>

          <div className="erp-group-h">Capability model</div>
          {conds.slice(0, 3).map((cond) => (
            <div
              key={cond.label}
              className={`erp-cond ${cond.ok ? 'erp-cond--met' : 'erp-cond--unmet'}`}
            >
              <ConditionMarker ok={cond.ok} />
              <span>
                {cond.label}
                {cond.hint && <span className="erp-cond__hint">{cond.hint}</span>}
              </span>
            </div>
          ))}

          <div className="erp-group-h">Scope and state</div>
          {conds.slice(3).map((cond) => (
            <div
              key={cond.label}
              className={`erp-cond ${cond.ok ? 'erp-cond--met' : 'erp-cond--unmet'}`}
            >
              <ConditionMarker ok={cond.ok} />
              <span>
                {cond.label}
                {cond.hint && <span className="erp-cond__hint">{cond.hint}</span>}
              </span>
            </div>
          ))}

          <div className="erp-setup-links">
            <button
              type="button"
              className="erp-link"
              onClick={() =>
                navigate(
                  `/erp_consolidation/capability-model?initiative=${encodeURIComponent(initiative.id)}`,
                )
              }
            >
              Open the capability model →
            </button>
            <button
              type="button"
              className="erp-link"
              onClick={() => navigate('/erp_consolidation/business-units')}
            >
              Open business units →
            </button>
          </div>

          {/* HIDDEN, NOT DISABLED for a role that may not baseline (§17.3); DISABLED
              WITH THE REASON when the role is right but the gate is not met. */}
          {canBaseline && (
            <div className="erp-setup-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !allMet}
                title={gateReason}
                onClick={() => void baseline()}
              >
                {busy ? 'Baselining…' : 'Baseline initiative'}
              </button>
              {!allMet && <span className="erp-setup-reason">{gateReason}</span>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * THE STANDALONE SCREEN, still routed by `ErpConsolidationModule.tsx:164`. Its chrome — the
 * page header naming the initiative, the "‹ All initiatives" link back, the help drawer, the
 * height lock and the single `.erp-setup-body` scroll region — is unchanged.
 */
export function InitiativeSetup({ initiativeId }: { initiativeId: string }) {
  const navigate = useNavigate();
  const { setRoot, rootStyle } = useScreenLock();

  /* Only for the header's subtitle. The checklist below holds the live copy. */
  const [initiative, setInitiative] = useState<Initiative | null>(null);

  return (
    <ErpScreen>
      <div className="page" ref={setRoot} style={rootStyle}>
        <PageHeader
          title="Initiative Setup"
          subtitle={
            initiative
              ? `${initiative.name} — what is still outstanding before it can be baselined`
              : 'What is still outstanding before this initiative can be baselined'
          }
          actions={
            <button
              type="button"
              className="erp-link"
              onClick={() => navigate('/erp_consolidation/initiatives')}
            >
              ‹ All initiatives
            </button>
          }
        />
        <HelpPanel help={SetupHelp} />

        <div className="erp-setup">
          <div className="erp-setup-body">
            <InitiativeSetupContent
              initiativeId={initiativeId}
              onInitiativeLoaded={setInitiative}
            />
          </div>
        </div>
      </div>
    </ErpScreen>
  );
}

export interface InitiativeSetupPanelProps {
  initiativeId: string;
  /** The row's own name, so the header can identify the record before the checklist loads. */
  initiativeName: string;
  onClose: () => void;
  onBaselined?: () => void;
}

/**
 * The same checklist as a RIGHT-SIDE PANEL — what the ⚙️ row action on the initiative list
 * opens. D-017's shell, SINGLE width: the content is one column of short condition lines, so
 * there is nothing here that cannot be read at 531px (`chrome/panelWidths.css` — DOUBLE is for
 * a side-by-side comparison or a table with more columns than fit, not for extra room).
 *
 * `hideSave` — the reason is in this file's header: the checklist's one action is gated, and the
 * gate cannot be expressed in the footer, so the action stays in the card and Cancel is the only
 * footer control. `dirty` is left at its default: there is nothing typed here to discard.
 */
export function InitiativeSetupPanel({
  initiativeId,
  initiativeName,
  onClose,
  onBaselined,
}: InitiativeSetupPanelProps) {
  return (
    <FormPanel
      open
      title="Initiative Setup"
      subtitle={`${initiativeName} — what is still outstanding before it can be baselined`}
      onRequestClose={onClose}
      /* Nothing to save: this is a derived checklist. `onSave` is required by the shell, and with
         `hideSave` there is no submit control to reach it — so it is a no-op rather than `onClose`,
         which would quietly turn an Enter keypress into "leave the panel" if a field ever
         appeared here. */
      onSave={() => undefined}
      hideSave
      /* "Close", not "Cancel". With no primary there is nothing pending, and "Cancel" would ask the
         user to abandon a change they were never offered. `FormPanel.tsx`'s `cancelLabel` exists for
         exactly this panel. */
      cancelLabel="Close"
    >
      <div className="erp-setup-panel-body">
        <InitiativeSetupContent initiativeId={initiativeId} onBaselined={onBaselined} />
      </div>
    </FormPanel>
  );
}
